import React, { useContext, useState } from "react";
import { ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";

import useFarms from "../../hooks/use-farms.js";
import useStrategicAccounts from "../../hooks/use-strategic-accounts.js";
import { LoginContext } from "../../context/login-context";
import usePersistedState from "../../hooks/use-persisted-state.js";

import Kanban from "./kanban.js";
import InvoicesGrid from "./InvoicesGrid.js";

const VIEWS = { GRID: "grid", KANBAN: "kanban" };

/* Shared sx props for each ToggleButton */
const toggleBtnSx = {
	px: "14px",
	py: "7px",
	gap: "6px",
	textTransform: "none",
	fontSize: "14px",
	fontWeight: 600,
	fontFamily: "var(--font-inter)",
	lineHeight: "20px",
	color: "var(--text-secondary-700)",
	backgroundColor: "var(--gray-0)",
	transition: "background-color 0.15s ease, color 0.15s ease",
	"&.Mui-selected": {
		backgroundColor: "var(--border-brand-solid)",
		color: "#fff",
		"&:hover": {
			backgroundColor: "#1F5A7D",
		},
	},
	"&:hover:not(.Mui-selected)": {
		backgroundColor: "var(--background-active-bg)",
	},
	"& .MuiSvgIcon-root": {
		fontSize: "18px",
	},
};

const Home = ({ showUpload, setShowUpload }) => {
	const { farms } = useFarms();
	const { strategicAccounts, flattenStratAccounts } = useStrategicAccounts();
	const { state: userData } = useContext(LoginContext);
	const [view, setView] = usePersistedState('dashView', VIEWS.KANBAN);

	const sharedProps = {
		strategicAccounts,
		flattenStratAccounts,
		farms,
		showUpload,
		setShowUpload,
	};

	if (!userData?.isValmontUser) {
		return <Kanban {...sharedProps} />;
	}

	const viewToggle = (
		<ToggleButtonGroup
			value={view}
			exclusive
			onChange={(_, newView) => {
				if (newView !== null) setView(newView);
			}}
			aria-label="invoice view"
			sx={{
				border: "1px solid var(--border-primary)",
				borderRadius: "8px",
				overflow: "hidden",
				boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
				"& .MuiToggleButtonGroup-grouped": {
					border: "none",
					borderRadius: 0,
					"&:not(:first-of-type)": {
						borderLeft: "1px solid var(--border-primary)",
						marginLeft: 0,
					},
				},
			}}
		>
			<Tooltip title="Board view" placement="bottom">
				<ToggleButton
					value={VIEWS.KANBAN}
					aria-label="board view"
					sx={toggleBtnSx}
				>
					<ViewKanbanOutlinedIcon />
					Board
				</ToggleButton>
			</Tooltip>

			<Tooltip title="Table view" placement="bottom">
				<ToggleButton
					value={VIEWS.GRID}
					aria-label="table view"
					sx={toggleBtnSx}
				>
					<TableRowsOutlinedIcon />
					Table
				</ToggleButton>
			</Tooltip>
		</ToggleButtonGroup>
	);

	return (
		<>
			{view === VIEWS.GRID ? (
				<InvoicesGrid {...sharedProps} filterAddon={viewToggle} />
			) : (
				<Kanban {...sharedProps} filterAddon={viewToggle} />
			)}
		</>
	);
};

export default Home;

